export interface CameraOptions {
  width: number;
  height: number;
  facingMode: 'user' | 'environment';
}

export interface CameraResolution {
  width: number;
  height: number;
}

export class CameraManager {
  private stream: MediaStream | null = null;

  constructor(private readonly video: HTMLVideoElement) {}

  async start(opts: CameraOptions): Promise<CameraResolution> {
    if (!window.isSecureContext) {
      throw new Error('getUserMedia requires a secure context (HTTPS or localhost).');
    }

    this.stop();
    this.video.removeAttribute('src');
    this.video.load();
    this.video.loop = false;

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        width: { ideal: opts.width },
        height: { ideal: opts.height },
        facingMode: opts.facingMode,
      },
    });

    this.video.srcObject = this.stream;
    await this.waitForData();
    await this.video.play();

    const track = this.stream.getVideoTracks()[0];
    const settings = track.getSettings();
    return {
      width: settings.width ?? this.video.videoWidth,
      height: settings.height ?? this.video.videoHeight,
    };
  }

  stop(): void {
    if (!this.stream) return;
    for (const track of this.stream.getTracks()) track.stop();
    this.stream = null;
    this.video.srcObject = null;
  }

  private waitForData(): Promise<void> {
    return new Promise((resolve) => {
      if (this.video.readyState >= 2) {
        resolve();
        return;
      }
      this.video.onloadeddata = () => resolve();
    });
  }
}
